import { apiClient } from './api-client';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type DataElementRequestType = 'DATA_ACCESS' | 'DATA_CORRECTION' | 'DATA_ERASURE' | 'DATA_PORTABILITY';

export interface DataElementSummary {
    dataElementId: number;
    dataElementKey: string;
    elementName: string;
    elementDescription: string | null;
    elementType: string | null;
    isIdentifier: boolean;
    isPersonalData: boolean;
    latestValue: string | null;
    latestCollectionDate: string | null;
    collectionPointCount: number;
    totalRequests: number;
    openRequests: number;
    latestConsentHeaderId: number | null;
    latestVaultId: number | null;
}

export type DataElementTimelineEventType = 'COLLECTION' | 'REQUEST';

export interface DataElementTimelineEvent {
    eventType: DataElementTimelineEventType;
    eventDate: string;
    title: string;
    description?: string | null;
    metadata?: Record<string, any>;
}

export interface DataElementRequestSummary {
    requestId: number;
    requestType: DataElementRequestType;
    requestStatus: string;
    description: string | null;
    reason: string | null;
    createdDate: string;
    estimatedCompletionDate: string | null;
    actualCompletionDate: string | null;
}

export interface DataElementDetail extends DataElementSummary {
    timeline: DataElementTimelineEvent[];
    requests: DataElementRequestSummary[];
}

export interface DataElementSummaryResponse {
    success: boolean;
    data: DataElementSummary[];
    error?: string;
}

export interface DataElementDetailResponse {
    success: boolean;
    data: DataElementDetail;
    error?: string;
}

export interface CreateDataElementRequestPayload {
    requestType: DataElementRequestType;
    description: string;
    reason?: string;
    dataElementId: number;
    dataElementKey?: string;
}

export interface CreateDataElementRequestResponse {
    success: boolean;
    data: {
        requestId: number;
        status: string;
        submittedDate: string;
        estimatedCompletionDate: string;
        message: string;
    };
    error?: string;
    errorType?: string;
}

// ============================================================================
// DATA ELEMENT SERVICE
// ============================================================================

class DataService {
    private baseUrl: string;

    constructor() {
        this.baseUrl = '/portal/data-elements';
    }

    /**
     * Fetch summary list of data elements for the authenticated data principal.
     */
    async getDataElementsSummary(): Promise<DataElementSummary[]> {
        try {
            const response = await apiClient.get<DataElementSummaryResponse>(this.baseUrl);

            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch data element summary');
            }

            return response.data;
        } catch (error) {
            console.error('Error fetching data element summary:', error);
            throw error;
        }
    }

    /**
     * Fetch detailed information and timeline for a specific data element.
     */
    async getDataElementDetail(dataElementId: number): Promise<DataElementDetail> {
        try {
            const url = `${this.baseUrl}/${dataElementId}`;
            const response = await apiClient.get<DataElementDetailResponse>(url);

            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch data element detail');
            }

            return response.data;
        } catch (error) {
            console.error(`Error fetching data element detail (${dataElementId}):`, error);
            throw error;
        }
    }

    /**
     * Create a new DP rights request scoped to a specific data element.
     * The backend expects requestedData to contain dataElementIds for tracking.
     */
    async createDataElementRequest(payload: CreateDataElementRequestPayload): Promise<CreateDataElementRequestResponse> {
        try {
            const url = '/portal/rights-requests';
            const requestBody = {
                requestType: payload.requestType,
                description: payload.description,
                reason: payload.reason,
                requestedData: {
                    dataElementIds: [payload.dataElementId],
                    dataElementKeys: payload.dataElementKey ? [payload.dataElementKey] : undefined
                }
            };

            const response = await apiClient.post<CreateDataElementRequestResponse>(url, requestBody);

            if (!response.success) {
                throw new Error(response.error || 'Failed to create data element request');
            }

            return response;
        } catch (error) {
            console.error('Error creating data element request:', error);
            throw error;
        }
    }
}

export const dataService = new DataService();

export default dataService;
